import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Send, Video, Loader2, Quote, X, Upload, Pencil, Trash2, Eye, EyeOff, Filter, Save, Shield } from "lucide-react";
import axios from "axios";
import { PublicHeader, PublicFooter } from "@/pages/BlogPage";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const STATUS_META = {
    approved: { label: "Publicado", color: "bg-[var(--status-success-bg)] text-[var(--status-success)]" },
    pending: { label: "Pendente", color: "bg-[var(--status-warning-bg)] text-[var(--status-warning)]" },
    rejected: { label: "Rejeitado", color: "bg-[var(--status-danger-bg)] text-[var(--status-danger)]" },
};

export default function TestimonialsPage() {
    const { user } = useAuth();
    const isPrivileged = !!user && (user.role === "doctor" || user.role === "admin");
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [statusFilter, setStatusFilter] = useState("all"); // admin-only filter
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = isPrivileged
                ? await api.get("/admin/testimonials")
                : await publicApi.get("/testimonials");
            setList(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [isPrivileged]); // eslint-disable-line react-hooks/exhaustive-deps

    const displayList = isPrivileged && statusFilter !== "all"
        ? list.filter((t) => t.status === statusFilter)
        : list;

    const counts = isPrivileged
        ? { all: list.length, approved: 0, pending: 0, rejected: 0 }
        : null;
    if (counts) list.forEach((t) => { counts[t.status] = (counts[t.status] || 0) + 1; });

    return (
        <div className="min-h-screen bg-base">
            <PublicHeader />

            <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-10">
                <div className="grid md:grid-cols-2 gap-8 items-end mb-8">
                    <div>
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-alt text-brand text-xs font-semibold uppercase tracking-widest mb-4">
                            <Quote size={12} /> Vozes de quem cuidamos
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-brand tracking-tight leading-[1.05]">
                            Depoimentos
                        </h1>
                        <p className="mt-4 text-lg text-[var(--text-secondary)]">
                            Histórias reais de pacientes e famílias que confiam seu cuidado à nossa equipe.
                        </p>
                    </div>
                    <div className="flex md:justify-end">
                        <button data-testid="open-testimonial-form" onClick={() => setShowForm(true)} className="btn-primary">
                            <Send size={14} /> Deixar meu depoimento
                        </button>
                    </div>
                </div>

                {isPrivileged && (
                    <div
                        data-testid="testimonials-admin-toolbar"
                        className="card-earthy mb-6 bg-alt/60 border-l-4 border-brand flex flex-wrap items-center gap-3 justify-between"
                    >
                        <div className="flex items-center gap-2 text-sm text-brand">
                            <Shield size={16} /> <strong>Modo administrador</strong>
                            <span className="text-muted-clinical">— você vê todos os depoimentos (publicados, pendentes e rejeitados) e pode editar ou excluir cada um.</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                            <Filter size={12} className="text-muted-clinical" />
                            {["all", "approved", "pending", "rejected"].map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setStatusFilter(s)}
                                    data-testid={`admin-filter-${s}`}
                                    className={`px-2.5 py-1 rounded-full transition-colors ${statusFilter === s ? "bg-brand text-white" : "text-brand-secondary hover:bg-white"}`}
                                >
                                    {s === "all" ? "Todos" : STATUS_META[s].label}{" "}
                                    <span className="opacity-70">({counts?.[s] || 0})</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {loading ? <p className="text-muted-clinical">Carregando…</p> : displayList.length === 0 ? (
                    <div className="card-earthy text-sm text-muted-clinical" data-testid="testimonials-empty">
                        {isPrivileged
                            ? "Nenhum depoimento neste filtro."
                            : "Ainda não temos depoimentos publicados. Seja o primeiro!"}
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {displayList.map((t) => (
                            <TestimonialCard
                                key={t.id}
                                t={t}
                                isPrivileged={isPrivileged}
                                onEdit={() => setEditing(t)}
                                onReload={load}
                            />
                        ))}
                    </div>
                )}
            </section>

            {showForm && <TestimonialForm onClose={() => setShowForm(false)} onSubmitted={load} />}
            {editing && (
                <AdminEditModal
                    testimonial={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}

            <PublicFooter />
        </div>
    );
}

function TestimonialCard({ t, isPrivileged, onEdit, onReload }) {
    const isFile = t.video_url && t.video_url.startsWith("/api");
    const [busy, setBusy] = useState(false);

    const quickToggle = async () => {
        const next = t.status === "approved" ? "pending" : "approved";
        setBusy(true);
        try {
            await api.put(`/admin/testimonials/${t.id}`, { status: next });
            toast.success(next === "approved" ? "Depoimento publicado." : "Depoimento oculto (pendente).");
            onReload();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const remove = async () => {
        if (!window.confirm(`Excluir permanentemente o depoimento de "${t.author_name}"?`)) return;
        setBusy(true);
        try {
            await api.delete(`/admin/testimonials/${t.id}`);
            toast.success("Depoimento excluído.");
            onReload();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const badge = STATUS_META[t.status];

    return (
        <div
            data-testid={`testimonial-${t.id}`}
            className={`card-earthy relative ${t.status !== "approved" ? "opacity-90" : ""}`}
        >
            {isPrivileged && (
                <div className="absolute top-3 right-3 flex items-center gap-1">
                    <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full ${badge.color}`}>
                        {badge.label}
                    </span>
                </div>
            )}
            {t.video_url && isFile && (
                <video controls src={`${BACKEND_URL}${t.video_url}`} className="w-full rounded-lg bg-black mb-3" />
            )}
            {t.video_url && !isFile && (
                <div className="rounded-lg overflow-hidden mb-3 aspect-video bg-black">
                    <iframe src={t.video_url} title={t.author_name} className="w-full h-full" allowFullScreen />
                </div>
            )}
            <Quote size={22} className="text-[var(--brand-accent)] opacity-30 mb-2" />
            <p className="text-sm text-[var(--text-primary)] leading-relaxed mb-4 whitespace-pre-wrap">{t.body}</p>
            <div className="text-xs text-brand font-medium">{t.author_name}</div>
            <div className="text-[10px] text-muted-clinical uppercase tracking-wider mt-0.5">
                {t.approved_at ? new Date(t.approved_at).toLocaleDateString("pt-BR") : (t.created_at ? new Date(t.created_at).toLocaleDateString("pt-BR") : "")}
            </div>

            {isPrivileged && (
                <div
                    className="mt-4 pt-3 border-t border-[var(--border-organic)] flex flex-wrap gap-2"
                    data-testid={`testimonial-admin-controls-${t.id}`}
                >
                    <button
                        onClick={quickToggle}
                        disabled={busy}
                        data-testid={`testimonial-toggle-${t.id}`}
                        className="btn-ghost text-xs"
                        title={t.status === "approved" ? "Ocultar (mover para pendentes)" : "Publicar"}
                    >
                        {t.status === "approved" ? <><EyeOff size={12} /> Ocultar</> : <><Eye size={12} /> Publicar</>}
                    </button>
                    <button
                        onClick={onEdit}
                        disabled={busy}
                        data-testid={`testimonial-edit-${t.id}`}
                        className="btn-ghost text-xs"
                    >
                        <Pencil size={12} /> Editar
                    </button>
                    <button
                        onClick={remove}
                        disabled={busy}
                        data-testid={`testimonial-delete-${t.id}`}
                        className="text-xs px-2.5 py-1.5 rounded-lg text-[var(--status-danger)] hover:bg-[var(--status-danger-bg)] transition-colors flex items-center gap-1"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Excluir
                    </button>
                </div>
            )}
        </div>
    );
}

function AdminEditModal({ testimonial, onClose, onSaved }) {
    const [form, setForm] = useState({
        author_name: testimonial.author_name || "",
        body: testimonial.body || "",
        video_url: testimonial.video_url || "",
        status: testimonial.status || "pending",
    });
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.put(`/admin/testimonials/${testimonial.id}`, {
                author_name: form.author_name.trim(),
                body: form.body.trim(),
                video_url: form.video_url.trim() || null,
                status: form.status,
            });
            toast.success("Depoimento atualizado.");
            onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="testimonial-edit-modal">
            <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full">
                <div className="p-5 border-b border-[var(--border-organic)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Pencil size={18} className="text-brand" />
                        <h2 className="font-display text-xl font-medium text-brand">Editar depoimento</h2>
                    </div>
                    <button onClick={onClose} className="text-muted-clinical hover:text-brand"><X size={18} /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="label-clinical">Nome do autor</label>
                        <input
                            type="text"
                            value={form.author_name}
                            onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                            className="input-clinical"
                            data-testid="edit-testimonial-name"
                        />
                    </div>
                    <div>
                        <label className="label-clinical">Depoimento</label>
                        <textarea
                            rows={6}
                            value={form.body}
                            onChange={(e) => setForm({ ...form, body: e.target.value })}
                            className="input-clinical"
                            data-testid="edit-testimonial-body"
                        />
                    </div>
                    <div>
                        <label className="label-clinical">URL do vídeo (opcional)</label>
                        <input
                            type="text"
                            value={form.video_url}
                            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                            placeholder="https://www.youtube.com/embed/..."
                            className="input-clinical font-mono text-xs"
                            data-testid="edit-testimonial-video"
                        />
                    </div>
                    <div>
                        <label className="label-clinical">Status</label>
                        <select
                            value={form.status}
                            onChange={(e) => setForm({ ...form, status: e.target.value })}
                            className="input-clinical"
                            data-testid="edit-testimonial-status"
                        >
                            <option value="approved">Publicado</option>
                            <option value="pending">Pendente</option>
                            <option value="rejected">Rejeitado</option>
                        </select>
                    </div>
                </div>
                <div className="p-4 border-t border-[var(--border-organic)] flex justify-end gap-2">
                    <button onClick={onClose} className="btn-secondary" disabled={busy}>Cancelar</button>
                    <button onClick={save} disabled={busy || !form.body.trim() || !form.author_name.trim()} className="btn-primary" data-testid="save-testimonial-btn">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
                    </button>
                </div>
            </div>
        </div>
    );
}

function TestimonialForm({ onClose, onSubmitted }) {
    const [form, setForm] = useState({ author_name: "", author_email: "", body: "" });
    const [videoUrl, setVideoUrl] = useState("");
    const [uploading, setUploading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef(null);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await publicApi.post("/testimonials/upload-video", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setVideoUrl(data.url);
            toast.success("Vídeo enviado.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Falha no upload");
        } finally { setUploading(false); }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await publicApi.post("/testimonials", { ...form, author_email: form.author_email?.trim() || null, video_url: videoUrl || null });
            toast.success("Depoimento enviado!");
            onSubmitted && onSubmitted();
            onClose();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Falha ao enviar");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 my-8 relative">
                <button onClick={onClose} className="absolute top-3 right-3 text-muted-clinical hover:text-brand"><X size={16} /></button>
                <h3 className="font-display text-2xl font-medium text-brand mb-1">Deixe seu depoimento</h3>
                <p className="text-sm text-muted-clinical mb-5">Compartilhe sua experiência com a clínica.</p>
                <form onSubmit={submit} className="space-y-3">
                    <div>
                        <label className="label-clinical">Seu nome *</label>
                        <input data-testid="testimonial-name" required value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} className="input-clinical" />
                    </div>
                    <div>
                        <label className="label-clinical">E-mail (opcional)</label>
                        <input data-testid="testimonial-email" type="email" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} className="input-clinical" />
                    </div>
                    <div>
                        <label className="label-clinical">Seu depoimento *</label>
                        <textarea data-testid="testimonial-body" required rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="input-clinical" placeholder="Conte como foi sua experiência..." />
                    </div>
                    <div>
                        <label className="label-clinical">Anexar vídeo (opcional, até 50 MB)</label>
                        <div className="flex items-center gap-2">
                            <input ref={fileRef} type="file" accept="video/mp4,video/quicktime,video/webm" onChange={upload} className="hidden" data-testid="testimonial-file-input" />
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-secondary text-sm">
                                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {videoUrl ? "Substituir vídeo" : "Enviar vídeo"}
                            </button>
                            {videoUrl && (
                                <span className="text-xs text-brand-secondary flex items-center gap-1">
                                    <Video size={12} /> Vídeo anexado
                                </span>
                            )}
                        </div>
                    </div>
                    <button data-testid="testimonial-submit" disabled={submitting || uploading} className="btn-primary w-full mt-2">
                        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar depoimento
                    </button>
                </form>
            </div>
        </div>
    );
}
